import { useState, useEffect, useRef, useMemo, startTransition } from 'react';
import { Button } from '../ui/button';
import { Search, Loader2, ChevronDown, Calendar } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import { api } from '../../services/api';
import { STOCK_LIST, loadStockList, searchStocks } from '../../data/stockList';
import { loadDoneDetails, loadOhlcByPath, parseDoneDetails } from '../../services/duckdbService';
import { fetchZipEntry } from '../../utils/zipRangeReader';
import { resolveBoBundleUrls } from '../../utils/boBundleBlob';
import { getOhlcBlobPath } from '../../utils/sectorMappingCache';
import { fetchBrokerList } from '../../utils/brokerMetadataBlob';
import { menuPreferencesService } from '../../services/menuPreferences';
import { useProfile } from '../../contexts/ProfileContext';

// Local Pivot Table Component (Internal customized dependencies)
import PivotTableUI from './dd-pivot-table/PivotTableUI';
import './dd-pivot-table/pivottable.css';
import TableRenderers from './dd-pivot-table/TableRenderers';
import { aggregators as allAggregators } from './dd-pivot-table/Utilities';

// --- Filter aggregators to only include: Sum, Count, Average, Minimum, Maximum ---

const filteredAggregators = {
  Sum: allAggregators['Sum'],
  Count: allAggregators['Count'],
  Average: allAggregators['Average'],
  Minimum: allAggregators['Minimum'],
  Maximum: allAggregators['Maximum'],
};

interface DoneDetailData {
  SELLER: string;
  BUYER: string;
  'F/D SELL': string;
  'F/D BUY': string;
  'SO No': number;
  'BO No': number;
  TIME: string;
  VALUE: number;
  VOLUME: number;
  PRICE: number;
  STK_CODE: string;
  DATE: string;
  'HAKA HAKI': string;
  SESSION: number;
  TYP: string;
  INV_TYP1: string;
  INV_TYP2: string;
  BOARD: string;
  [key: string]: any;
}

const PAGE_ID = 'done-detail-v6';

// Fixed row order when Group is ON — cannot be reordered or removed by user
const LOCKED_GROUP_ROWS = ['BO No', 'SO No', 'TIME', 'PRICE', 'BUYER', 'SELLER'];

// Custom high-performance sorters to bypass naturalSort for heavy columns
const customSorters: Record<string, (a: any, b: any) => number> = {
  TIME: (a, b) => {
    if (a === b) return 0;
    if (a === null || a === undefined) return -1;
    if (b === null || b === undefined) return 1;
    return a < b ? -1 : 1;
  },
  'BO No': (a, b) => {
    if (a === null || a === undefined) return -1;
    if (b === null || b === undefined) return 1;
    return Number(a) - Number(b);
  },
  'SO No': (a, b) => {
    if (a === null || a === undefined) return -1;
    if (b === null || b === undefined) return 1;
    return Number(a) - Number(b);
  },
  PRICE: (a, b) => {
    if (a === null || a === undefined) return -1;
    if (b === null || b === undefined) return 1;
    return Number(a) - Number(b);
  },
  VALUE: (a, b) => {
    if (a === null || a === undefined) return -1;
    if (b === null || b === undefined) return 1;
    return Number(a) - Number(b);
  },
  VOLUME: (a, b) => {
    if (a === null || a === undefined) return -1;
    if (b === null || b === undefined) return 1;
    return Number(a) - Number(b);
  },
  SESSION: (a, b) => {
    if (a === null || a === undefined) return -1;
    if (b === null || b === undefined) return 1;
    return Number(a) - Number(b);
  },
  DATE: (a, b) => {
    if (a === b) return 0;
    if (a === null || a === undefined) return -1;
    if (b === null || b === undefined) return 1;
    return a < b ? -1 : 1;
  }
};

// Interface for user preferences
interface UserPreferences {
  selectedStock: string;
  targetDate?: string;
  selectedBoard: string;
  selectedInvType: string;
  selectedBrokers?: string[];
  selectedBroker?: string;
  startTime: string;
  endTime: string;
  priceFilter: string;
  pivotState?: any;
}

const loadPreferences = (pageId: string): Partial<UserPreferences> | null => {
  try {
    const cached = menuPreferencesService.getCachedPreferences(pageId);
    if (cached) {
      return cached as Partial<UserPreferences>;
    }
    const prefs = menuPreferencesService.loadPreferences(pageId);
    return prefs ? prefs as Partial<UserPreferences> : null;
  } catch (error) {
    console.warn('Failed to load cached preferences:', error);
  }
  return null;
};

const savePreferences = (pageId: string, prefs: Partial<UserPreferences>) => {
  menuPreferencesService.savePreferences(pageId, prefs);
};



// Same lists as Broker Summary 5D (NewBrokerSummary.tsx)
const GOVERNMENT_BROKERS = ['CC', 'NI', 'OD', 'DX'];
const FOREIGN_BROKERS = [
  "AG", "AH", "AI", "AK", "BK", "BQ", "CG", "CS", "DP", "DR", "DU", "FS", "GW", "HD", "KK",
  "KZ", "LH", "LG", "LS", "MS", "RB", "RX", "TX", "YP", "YU", "ZP"
];

const getBrokerColorClass = (brokerCode: string): { color: string; className: string } => {
  if (GOVERNMENT_BROKERS.includes(brokerCode)) {
    return { color: '#10B981', className: 'font-semibold' }; // Green-600
  }
  if (FOREIGN_BROKERS.includes(brokerCode)) {
    return { color: '#EF4444', className: 'font-semibold' }; // Red-600
  }
  return { color: '#FFFFFF', className: 'font-semibold' }; // White
};

interface BrokerSelectorProps {
  selectedBrokers: string[];
  setSelectedBrokers: React.Dispatch<React.SetStateAction<string[]>>;
  availableBrokers: string[];
  isTrial: boolean;
  showToast: (options: { type: 'success' | 'error' | 'warning' | 'info'; title: string; message: string }) => void;
}

function BrokerSelector({
  selectedBrokers,
  setSelectedBrokers,
  availableBrokers,
  isTrial,
  showToast
}: BrokerSelectorProps) {
  const [brokerSearch, setBrokerSearch] = useState('');
  const [showBrokerSuggestions, setShowBrokerSuggestions] = useState(false);
  const dropdownBrokerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownBrokerRef.current && !dropdownBrokerRef.current.contains(event.target as Node)) {
        setShowBrokerSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredBrokers = useMemo(() => {
    if (!brokerSearch) return availableBrokers;
    return availableBrokers.filter(b => b.toLowerCase().includes(brokerSearch.toLowerCase()));
  }, [availableBrokers, brokerSearch]);

  return (
    <div className="relative" ref={dropdownBrokerRef}>
      <div
        className={`w-24 h-9 px-3 flex items-center justify-between bg-background border border-[#3a4252] rounded-md text-sm text-white cursor-pointer hover:border-primary/50 ${isTrial ? 'opacity-80' : ''}`}
        onClick={() => {
          if (isTrial) {
            showToast({
              type: 'warning',
              title: 'Fitur Terbatas',
              message: 'Dalam masa free trial, Anda tidak dapat memilih broker spesifik.'
            });
            return;
          }
          setShowBrokerSuggestions(!showBrokerSuggestions);
        }}
      >
        <span className="truncate">
          {selectedBrokers.includes('ALL') ? 'All' : selectedBrokers.join(', ')}
        </span>
        <ChevronDown className="w-3 h-3 text-muted-foreground opacity-50 ml-1 flex-shrink-0" />
      </div>

      {!isTrial && showBrokerSuggestions && (
        <div className="absolute top-full left-0 mt-1 bg-popover border border-[#3a4252] rounded-md shadow-lg z-50 w-48 overflow-hidden flex flex-col">
          {selectedBrokers.length > 0 && !selectedBrokers.includes('ALL') && (
            <div className="px-3 py-2 text-xs text-muted-foreground border-b border-[#3a4252] flex items-center justify-between bg-popover">
              <span>Selected ({selectedBrokers.length})</span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedBrokers(['ALL']);
                }}
                className="text-xs text-destructive hover:text-destructive/80 font-medium"
              >
                Clear
              </button>
            </div>
          )}
          <div className="p-2 border-b border-[#3a4252]">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <input
                type="text"
                className="w-full h-8 pl-7 pr-2 bg-[#0a0f20] border border-[#3a4252] rounded text-sm text-white focus:outline-none focus:border-primary/50"
                placeholder="Search broker..."
                value={brokerSearch}
                onChange={(e) => setBrokerSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            </div>
          </div>
          <div className="max-h-60 overflow-y-auto py-1">
            {brokerSearch === '' && (
              <div
                className={`px-3 py-[2.06px] text-sm cursor-pointer flex justify-between items-center ${selectedBrokers.includes('ALL') ? 'text-primary font-medium' : 'text-white'}`}
                onClick={() => setSelectedBrokers(['ALL'])}
              >
                <span>ALL</span>
                {selectedBrokers.includes('ALL') && <span className="text-primary text-xs">✓</span>}
              </div>
            )}
            {filteredBrokers.slice(0, 30).map(broker => {
              const isSelected = selectedBrokers.includes(broker);
              const brokerColor = getBrokerColorClass(broker);
              return (
                <div
                  key={broker}
                  className={`px-3 py-[2.06px] text-sm cursor-pointer flex justify-between items-center ${isSelected ? 'font-medium bg-accent/50' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedBrokers(prev => {
                      const newSelection = prev.filter(b => b !== 'ALL');
                      if (isSelected) {
                        return newSelection.filter(b => b !== broker);
                      } else {
                        return [...newSelection, broker];
                      }
                    });
                  }}
                >
                  <span className={brokerColor.className} style={{ color: isSelected ? undefined : brokerColor.color }}>{broker}</span>
                  {isSelected && <span className="text-primary text-xs">✓</span>}
                </div>
              );
            })}
            {filteredBrokers.length > 30 && (
              <div className="px-3 py-1.5 text-xs text-muted-foreground border-t border-[#3a4252] text-center">
                ... and {filteredBrokers.length - 30} more brokers
              </div>
            )}
            {filteredBrokers.length === 0 && (
              <div className="px-3 py-2 text-sm text-muted-foreground text-center">No brokers found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

interface StockTransactionDoneDetailProps {
  sidebarOpen?: boolean;
  selectedStock?: string;
  isDashboard?: boolean;
  isActive?: boolean;
}

export function StockTransactionDoneDetail({ sidebarOpen, selectedStock: propSelectedStock, isDashboard = false, isActive = true }: StockTransactionDoneDetailProps) {
  const { showToast } = useToast();
  const { profile } = useProfile();
  const isTrial = profile?.subscriptionStatus === 'trial';
  const effectivePageId = isDashboard ? `dashboard-${PAGE_ID}` : PAGE_ID;
  const [savedPrefs] = useState(() => loadPreferences(effectivePageId));

  // Date setup
  const getPreviousBusinessDay = (): string => {
    const today = new Date();
    let targetDay = new Date(today);
    
    // If it's before 20:00, use the previous day as the starting point
    if (today.getHours() < 20) {
      targetDay.setDate(today.getDate() - 1);
    }
    
    // Fall back to Friday if we landed on a weekend
    while (targetDay.getDay() === 0 || targetDay.getDay() === 6) {
      targetDay.setDate(targetDay.getDate() - 1);
    }
    
    const year = targetDay.getFullYear();
    const month = String(targetDay.getMonth() + 1).padStart(2, '0');
    const day = String(targetDay.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const defaultDate = getPreviousBusinessDay();

  // Robustly derive dates from initial preference or default
  const initialDates = useMemo(() => {
    const targetStr = savedPrefs?.targetDate || defaultDate;
    return [targetStr];
  }, [savedPrefs?.targetDate, defaultDate]);

  const isSameStock = savedPrefs?.selectedStock === propSelectedStock;

  const [targetDate, setTargetDate] = useState(() => {
    // Try to load from preferences first (unless in dashboard mode and stock changed)
    if ((!isDashboard || isSameStock) && savedPrefs?.targetDate) {
      return savedPrefs.targetDate;
    }
    return defaultDate;
  });

  const [selectedStock, setSelectedStock] = useState(() => propSelectedStock || savedPrefs?.selectedStock || 'PTRO');
  const [stockInput, setStockInput] = useState(() => propSelectedStock || savedPrefs?.selectedStock || 'PTRO');

  useEffect(() => {
    if (propSelectedStock) {
      setSelectedStock(propSelectedStock);
      setStockInput(propSelectedStock);
    }
  }, [propSelectedStock]);

  // Data state
  const [_availableStocks] = useState<string[]>(STOCK_LIST);
  const [doneDetailData, setDoneDetailData] = useState<Map<string, DoneDetailData[]>>(new Map());

  const [isLoading, setIsLoading] = useState(false);
  const [isFullDataLoading, setIsFullDataLoading] = useState(false);
  const [_error, setError] = useState<string | null>(null);
  const [showStockSuggestions, setShowStockSuggestions] = useState(false);


  // Pivot Table State Migration & Initialization
  const initialPivotState = useMemo(() => {
    const defaultState = {
      rendererName: 'Table',
      aggregatorName: 'Sum',
      rows: ['BO No', 'SO No', 'TIME', 'PRICE', 'BUYER', 'SELLER'],
      cols: [],
      vals: ['VOLUME'],
      valueFilter: {
        'HAKA HAKI': { 'ALL': true }
      }
    };

    if (!savedPrefs?.pivotState) {
      // Ensure default rows are alphabetical even if no saved state
      return defaultState;
    }

    const state = { ...savedPrefs.pivotState };
    const mapping: Record<string, string> = {
      BRK_COD1: 'SELLER',
      BRK_COD2: 'BUYER',
      INV_TYP1: 'F/D SELL',
      INV_TYP2: 'F/D BUY',
      TRX_ORD1: 'SO No',
      TRX_ORD2: 'BO No',
      TRX_TIME: 'TIME',
      STK_VALUES: 'VALUE',
      STK_VOLM: 'VOLUME',
      STK_PRIC: 'PRICE',
      VALUE: 'VALUE',
      TRX_VALU: 'VALUE',
      TRX_DATE: 'DATE'
    };

    const migrate = (arr: string[]) => (arr || []).map(k => mapping[k] || k);

    if (state.rows) state.rows = migrate(state.rows).filter(x => x !== 'VOLUME');
    if (state.cols) state.cols = migrate(state.cols).filter(x => x !== 'VOLUME');
    state.vals = migrate(state.vals);
    if (!state.vals || state.vals.length === 0) {
      state.vals = ['VOLUME'];
    }

    if (!state.valueFilter) {
      state.valueFilter = { 'HAKA HAKI': { 'ALL': true } };
    } else if (!state.valueFilter['HAKA HAKI']) {
      state.valueFilter['HAKA HAKI'] = { 'ALL': true };
    }

    return state;
  }, [savedPrefs]);

  const [pivotState, setPivotState] = useState<any>(initialPivotState);

  // Global Filters
  const [selectedBoard, setSelectedBoard] = useState(savedPrefs?.selectedBoard || 'RG');
  const [selectedInvType, setSelectedInvType] = useState(savedPrefs?.selectedInvType || 'All');
  const [selectedBrokers, setSelectedBrokers] = useState<string[]>(() => {
    if (savedPrefs?.selectedBrokers && Array.isArray(savedPrefs.selectedBrokers) && savedPrefs.selectedBrokers.length > 0) {
      return savedPrefs.selectedBrokers;
    }
    if (savedPrefs?.selectedBroker && savedPrefs.selectedBroker !== 'All') {
      return [savedPrefs.selectedBroker];
    }
    return ['ALL'];
  });
  const [availableBrokers, setAvailableBrokers] = useState<string[]>([]);

  const [startTime, setStartTime] = useState(savedPrefs?.startTime || '08:58:00');
  const [endTime, setEndTime] = useState(savedPrefs?.endTime || '16:15:00');
  const [priceFilter, setPriceFilter] = useState(savedPrefs?.priceFilter || 'All');

  // Fetch available brokers
  useEffect(() => {
    let isMounted = true;
    const fetchBrokers = async () => {
      try {
        const cached = localStorage.getItem('broker-list-cache-v2');
        if (cached) {
          const parsed = JSON.parse(cached);
          if (parsed.brokers && isMounted) {
            setAvailableBrokers(parsed.brokers);
          }
        }
        const brokers = await fetchBrokerList();
        if (brokers.length > 0) {
          if (isMounted) setAvailableBrokers(brokers);
          localStorage.setItem('broker-list-cache-v2', JSON.stringify({
            brokers,
            timestamp: Date.now()
          }));
        }
      } catch (err) {}
    };
    fetchBrokers();
    return () => { isMounted = false; };
  }, []);

  // "Applied" states that trigger actual fetching/filtering
  const [appliedStock, setAppliedStock] = useState(selectedStock);
  const [appliedDate, setAppliedDate] = useState(targetDate);
  const [appliedStartTime, setAppliedStartTime] = useState(startTime);
  const [appliedEndTime, setAppliedEndTime] = useState(endTime);
  const [appliedPrice, setAppliedPrice] = useState(priceFilter);
  const [appliedBrokers, setAppliedBrokers] = useState(selectedBrokers);
  const [appliedBoard, setAppliedBoard] = useState(selectedBoard);
  const [appliedInvType, setAppliedInvType] = useState(selectedInvType);

  // Force default restrictions for trial users
  useEffect(() => {
    if (isTrial) {
      if (priceFilter !== 'All') {
        setPriceFilter('All');
      }
      if (appliedPrice !== 'All') {
        setAppliedPrice('All');
      }
      if (selectedBrokers.length !== 1 || selectedBrokers[0] !== 'ALL') {
        setSelectedBrokers(['ALL']);
      }
      if (appliedBrokers.length !== 1 || appliedBrokers[0] !== 'ALL') {
        setAppliedBrokers(['ALL']);
      }
    }
  }, [isTrial, priceFilter, appliedPrice, selectedBrokers, appliedBrokers]);


  // Group feature state
  const [groupActive, setGroupActive] = useState(false);
  const [hasExpandedRows, setHasExpandedRows] = useState(false);
  const mergeFields = useMemo(() => groupActive ? ['BO No', 'SO No'] : [], [groupActive]);
  // Saves rows layout before Group is activated so it can be restored when Group is turned OFF
  const preGroupRowsRef = useRef<string[]>([]);

  // Date and Time picker refs
  const dateRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const pivotAreaRef = useRef<HTMLDivElement>(null);



  // Dynamically size the pvtTableContainer to exactly fill remaining space
  useEffect(() => {
    const container = pivotAreaRef.current;
    if (!container) return;

    const resizeTableContainer = () => {
      const el = container.querySelector('.pvtTableContainer') as HTMLElement;
      if (!el) return;
      const containerRect = container.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();

      // Calculate offset from container's top to the element's top
      // This correctly handles cases where container is scrolled or has borders
      const offsetTop = elRect.top - containerRect.top;

      // clientHeight excludes the horizontal scrollbar height
      const availableHeight = container.clientHeight - offsetTop;

      if (availableHeight > 50) {
        // Subtract a tiny safety margin to absolutely prevent outer vertical scrollbars
        el.style.maxHeight = `${availableHeight - 2}px`;
        el.style.overflow = 'auto';
      }
    };

    // Run on mount and observe DOM changes (pivot re-renders)
    const mo = new MutationObserver(() => {
      requestAnimationFrame(resizeTableContainer);
    });
    mo.observe(container, { childList: true, subtree: true });

    // Also run on window resize
    const onResize = () => requestAnimationFrame(resizeTableContainer);
    window.addEventListener('resize', onResize);

    // Initial run
    requestAnimationFrame(resizeTableContainer);

    return () => {
      mo.disconnect();
      window.removeEventListener('resize', onResize);
    };
  }, []);

  // Load stock list on mount
  useEffect(() => {
    loadStockList();
  }, []);

  // Auto-trigger data fetch when isDashboard is true
  const dashboardAutoFetchRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isDashboard || !propSelectedStock || !isActive) return;
    if (dashboardAutoFetchRef.current === propSelectedStock) return;
    
    const timer = setTimeout(() => {
      if (targetDate) {
        dashboardAutoFetchRef.current = propSelectedStock;
        setAppliedStock(propSelectedStock);
        setAppliedDate(targetDate);
        setAppliedStartTime(startTime);
        setAppliedEndTime(endTime);
        setAppliedPrice(priceFilter);
        setAppliedBrokers(selectedBrokers);
        setAppliedBoard(selectedBoard);
        setAppliedInvType(selectedInvType);
        setSelectedStock(propSelectedStock);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [isDashboard, propSelectedStock, targetDate, startTime, endTime, priceFilter, selectedBrokers, selectedBoard, selectedInvType, isActive]);

  // Save preferences with debounce
  useEffect(() => {
    const timeout = setTimeout(() => {
      // Clean pivotState to avoid saving large data or non-serializable objects (functions)
      const cleanPivotState = { ...pivotState };
      delete cleanPivotState.data;
      delete cleanPivotState.renderers;
      delete cleanPivotState.aggregators;
      delete cleanPivotState.onRendererUpdate;
      delete cleanPivotState.sorters;
      delete cleanPivotState.derivedAttributes;

      const preferences: Partial<UserPreferences> = {
        selectedStock,
        selectedBoard,
        selectedInvType,
        selectedBrokers,
        startTime,
        endTime,
        priceFilter,
        pivotState: cleanPivotState,
      };
      if (startTime && endTime && !isDashboard) {
        preferences.startTime = startTime;
        preferences.endTime = endTime;
      }
      if (targetDate) preferences.targetDate = targetDate;
      savePreferences(effectivePageId, preferences);
    }, 500);
    return () => clearTimeout(timeout);
  }, [selectedStock, targetDate, selectedBoard, selectedInvType, selectedBrokers, startTime, endTime, priceFilter, pivotState]);


  // Auto-load data based on initial dates (one-time on mount or if preferences change external)
  useEffect(() => {
    if (initialDates.length > 0 && initialDates[0] && !appliedDate) {
      setAppliedDate(initialDates[0]);
    }
  }, [initialDates, appliedDate]);

  // Fetch Data - depends ONLY on applied stock and date
  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();

    const fetchData = async () => {
      if (!appliedStock || !appliedDate) return;

      console.log('Fetching data from Azure API:', { appliedStock, appliedDate });
      setIsLoading(true);
      setIsFullDataLoading(true);
      setError(null);

      try {
        // Clear previous close price and done details to avoid stale data while loading
        (window as any).__CURRENT_PREV_CLOSE__ = undefined;
        setPrevClose(undefined);
        setDoneDetailData(new Map());

        const tokenRes = await api.getOrderbookReplaySasToken();
        if (!isMounted) return;
        if (!tokenRes.success || !tokenRes.data) {
          throw new Error(tokenRes.error || 'Failed to authorize access.');
        }
        const { containerUrl, sasToken } = tokenRes.data;

        const dateFormatted = appliedDate.replace(/-/g, '');
        const { part1ZipUrl, fullZipUrl, parsedOhlc } = await resolveBoBundleUrls(
          containerUrl,
          sasToken,
          dateFormatted,
          appliedStock,
          abortController.signal
        );

        // Try to get prevClose from zip metadata or filename first
        let zipPrevClose: number | null = null;
        let metadataLoadedFromZip = false;

        if (parsedOhlc && typeof parsedOhlc.prevClose === 'number') {
          zipPrevClose = parsedOhlc.prevClose;
          metadataLoadedFromZip = true;
          console.log(`StockTransactionDoneDetail: Found prevClose from ZIP filename: ${zipPrevClose}`);
        } else {
          const targetZipUrl = part1ZipUrl || fullZipUrl;
          if (targetZipUrl) {
            try {
              console.log('StockTransactionDoneDetail: Fetching metadata.json from ZIP for prevClose...');
              const metadataBytes = await fetchZipEntry(targetZipUrl, 'metadata.json', abortController.signal);
              if (metadataBytes && isMounted) {
                const parsedMetadata = JSON.parse(new TextDecoder().decode(metadataBytes));
                console.log('StockTransactionDoneDetail: Parsed metadata.json from ZIP:', parsedMetadata);
                if (parsedMetadata && typeof parsedMetadata.prevClose === 'number') {
                  zipPrevClose = parsedMetadata.prevClose;
                  metadataLoadedFromZip = true;
                }
              }
            } catch (e) {
              console.warn('StockTransactionDoneDetail: Failed to fetch/parse metadata.json from ZIP:', e);
            }
          }
        }

        if (metadataLoadedFromZip && zipPrevClose !== null) {
          setPrevClose(zipPrevClose);
          (window as any).__CURRENT_PREV_CLOSE__ = zipPrevClose;
          console.log(`✅ Using prevClose from zip metadata: ${zipPrevClose}`);
        } else {
          // Fallback: fetch original OHLC parquet file
          console.log('StockTransactionDoneDetail: Fallback to OHLC Parquet for prevClose...');
          const earliestDate = appliedDate;
          if (earliestDate) {
            (async () => {
              try {
                const ohlcPath = await getOhlcBlobPath(appliedStock);
                if (!isMounted) return;
                if (!ohlcPath) {
                  throw new Error('No OHLC data found');
                }

                const ohlcRows = await loadOhlcByPath(ohlcPath, containerUrl, sasToken);
                if (!isMounted) return;

                const sortedData = [...ohlcRows].sort((a: any, b: any) => {
                  const dateA = a.Date || a.date || '';
                  const dateB = b.Date || b.date || '';
                  return dateB.localeCompare(dateA);
                });

                const prevData = sortedData.find((d: any) => {
                  const dDateStr = d.Date || d.date || '';
                  return dDateStr < earliestDate;
                });

                const closeVal = prevData ? (prevData.Close ?? prevData.close) : undefined;
                if (closeVal !== undefined) {
                  const finalPrevClose = parseFloat(String(closeVal));
                  setPrevClose(finalPrevClose);
                  (window as any).__CURRENT_PREV_CLOSE__ = finalPrevClose;
                  console.log(`✅ Found fallback prevClose for ${appliedStock}: ${finalPrevClose}`);
                } else {
                  setPrevClose(null);
                  (window as any).__CURRENT_PREV_CLOSE__ = null;
                }
              } catch (e) {
                console.error('Failed to fetch fallback prev close', e);
                if (!isMounted) return;
                setPrevClose(null);
                (window as any).__CURRENT_PREV_CLOSE__ = null;
              }
            })();
          }
        }

        // Process rows helper
        const mapFD = (val: string) => {
          if (val === 'I') return 'DOMESTIC';
          if (val === 'A') return 'FOREIGN';
          return val;
        };
        const formatTimeStr = (t: string | number) => {
          const s = String(t).padStart(6, '0');
          if (s.length > 6) return s;
          return `${s.substring(0, 2)}:${s.substring(2, 4)}:${s.substring(4, 6)}`;
        };

        const processRawRows = (rawRows: any[]) => {
          return rawRows.map((item: any) => {
            const soNo = item.TRX_ORD2 !== undefined && item.TRX_ORD2 !== null ? Number(item.TRX_ORD2) : 0;
            const boNo = item.TRX_ORD1 !== undefined && item.TRX_ORD1 !== null ? Number(item.TRX_ORD1) : 0;
            // Rupiah = shares × price. The parquet's VALUE column is shares × price / 100 (100x too small), so it is not used
            const valueVal = Number(item.STK_VOLM ?? 0) * Number(item.STK_PRIC ?? 0);
            const volVal = (Number(item.STK_VOLM ?? 0)) / 100;
            const priceVal = Number(item.STK_PRIC ?? 0);
            const hakaHakiVal = Number(item.HAKA_HAKI ?? 0) === 1 ? 'HAKA' : 'HAKI';
            const sessionVal = Number(item.TRX_SESS ?? 0);

            return {
              SELLER: item.BRK_COD2 ? String(item.BRK_COD2).trim() : '',
              BUYER: item.BRK_COD1 ? String(item.BRK_COD1).trim() : '',
              'F/D SELL': mapFD(item.INV_TYP2 ? String(item.INV_TYP2).trim() : ''),
              'F/D BUY': mapFD(item.INV_TYP1 ? String(item.INV_TYP1).trim() : ''),
              'SO No': soNo,
              'BO No': boNo,
              TIME: formatTimeStr(item.TRX_TIME ?? '0'),
              VALUE: valueVal,
              VOLUME: volVal,
              PRICE: priceVal,
              TYP: item.TRX_TYPE || item.TYP ? String(item.TRX_TYPE || item.TYP).trim() : '',
              INV_TYP1: item.INV_TYP1 ? String(item.INV_TYP1).trim() : '',
              INV_TYP2: item.INV_TYP2 ? String(item.INV_TYP2).trim() : '',
              BOARD: String(item.BOARD_COD ?? item.BOARD ?? item.BOARD_CODE ?? item.Board ?? item.TRX_TYPE ?? '').trim(),
              STK_CODE: String(item.STK_CODE ?? appliedStock),
              DATE: String(item.TRX_DATE ?? appliedDate),
              'HAKA HAKI': hakaHakiVal,
              SESSION: sessionVal,
            };
          });
        };

        let usedPart1 = false;
        let isFullLoaded = false;

        // Start full download promise (original parquet file)
        const fullPromise = loadDoneDetails(appliedStock, appliedDate, containerUrl, sasToken);

        // Start part1 ZIP download
        const part1Promise = fetchZipEntry(part1ZipUrl, 'done_detail.parquet', abortController.signal).catch((err) => {
          console.warn('Failed to fetch part1 ZIP done_detail:', err);
          return null;
        });

        // 1. Try loading and displaying part1 first
        try {
          const part1Bytes = await part1Promise;
          if (abortController.signal.aborted) return;
          if (part1Bytes && !isFullLoaded && isMounted) {
            console.log('StockTransactionDoneDetail: loaded from part1 zip. Parsing...');
            const part1Rows = await parseDoneDetails(part1Bytes.buffer as ArrayBuffer);
            if (abortController.signal.aborted) return;
            if (!isFullLoaded && isMounted) {
              const processedPart1 = processRawRows(part1Rows);
              const newData = new Map<string, DoneDetailData[]>();
              newData.set(appliedDate, processedPart1);
              setDoneDetailData(newData);
              usedPart1 = true;
              setIsLoading(false); // Hide the loading screen so user can see it fast!
              console.log('StockTransactionDoneDetail: Set part1 data successfully.');
            }
          }
        } catch (err) {
          console.warn('Failed to parse part1 done_detail:', err);
        }

        // 2. Load the full dataset (either in the background or as fallback)
        try {
          const fullRows = await fullPromise;
          isFullLoaded = true;
          if (abortController.signal.aborted) return;
          if (isMounted) {
            console.log('StockTransactionDoneDetail: loaded full done details. Parsing...');
            const processedFull = processRawRows(fullRows);
            const newData = new Map<string, DoneDetailData[]>();
            newData.set(appliedDate, processedFull);
            setDoneDetailData(newData);
            setIsLoading(false);
            setIsFullDataLoading(false);
            console.log('StockTransactionDoneDetail: Set full data successfully.');
          }
        } catch (err: any) {
          isFullLoaded = true;
          if (abortController.signal.aborted) return;
          setIsFullDataLoading(false);
          // If we haven't loaded part1 either, we must throw the error
          if (!usedPart1) {
            throw err;
          }
          console.warn('Full done details failed to load, but part1 was loaded:', err);
        }

      } catch (err: any) {
        console.error('Error loading data:', err);
        setError('Failed to load done detail data');
        showToast({ type: 'error', title: 'Error', message: err?.message || 'Failed to load data' });
      } finally {
        setIsLoading(false);
        setIsFullDataLoading(false);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [appliedStock, appliedDate, showToast]);




  const flatData = useMemo(() => {
    const allData = Array.from(doneDetailData.values()).flat();

    const isHakaHakiAll = pivotState?.valueFilter?.['HAKA HAKI']?.['HAKA'] && 
                          pivotState?.valueFilter?.['HAKA HAKI']?.['HAKI'] && 
                          !pivotState?.valueFilter?.['HAKA HAKI']?.['ALL'];

    const filteredData: any[] = [];
    const hasStartTimeEndTime = !!(appliedStartTime && appliedEndTime);
    const hasPrice = !!(appliedPrice && appliedPrice !== 'All');
    const hasBrokers = appliedBrokers.length > 0 && !appliedBrokers.includes('ALL');
    const hasBoard = appliedBoard !== 'All';
    const hasInvType = appliedInvType !== 'All';

    for (let i = 0; i < allData.length; i++) {
      const item = allData[i];
      if (!item) continue;
      
      if (hasStartTimeEndTime) {
        const t = String(item['TIME'] || '');
        if (t < appliedStartTime || t > appliedEndTime) continue;
      }
      
      if (hasPrice) {
        const p = item['PRICE'];
        if (!p || !String(p).includes(appliedPrice)) continue;
      }
      
      if (hasBrokers) {
        if (!appliedBrokers.includes(item['BUYER']) && !appliedBrokers.includes(item['SELLER'])) continue;
      }
      
      if (hasBoard) {
        if ((item.BOARD || item['TYP']) !== appliedBoard) continue;
      }
      
      // INV_TYP holds A (foreign) / I (domestic)
      if (hasInvType) {
        if (appliedInvType === 'Foreign') {
          if (item['INV_TYP1'] !== 'A' && item['INV_TYP2'] !== 'A') continue;
        } else if (appliedInvType === 'Domestic') {
          if (item['INV_TYP1'] !== 'I' || item['INV_TYP2'] !== 'I') continue;
        }
      }

      if (isHakaHakiAll) {
        filteredData.push({
          ...item,
          'HAKA HAKI': 'ALL'
        });
      } else {
        filteredData.push(item);
      }
    }

    return filteredData;
  }, [doneDetailData, appliedStartTime, appliedEndTime, appliedPrice, appliedBrokers, appliedBoard, appliedInvType, pivotState?.valueFilter]);

  // Merged/grouped data
  // Merged/grouped data
  const mergedData = useMemo(() => {
    if (mergeFields.length === 0) return flatData;

    // Fields whose values are summed across merged rows
    const SUM_FIELDS = new Set(['VOLUME', 'VALUE']);
    const isBSDominant = mergeFields.includes('BO No') && mergeFields.includes('SO No');

    const buckets = new Map<string, any[]>();
    const boVolumes = new Map<string, number>();
    const soVolumes = new Map<string, number>();

    if (isBSDominant) {
      // Pass 0: Calculate global volumes for each BO No and SO No
      for (let i = 0; i < flatData.length; i++) {
        const item = flatData[i];
        const bo = String(item['BO No'] ?? '');
        const so = String(item['SO No'] ?? '');
        const vol = Number(item['VOLUME']) || 0;

        if (bo && bo !== '0') boVolumes.set(bo, (boVolumes.get(bo) || 0) + vol);
        if (so && so !== '0') soVolumes.set(so, (soVolumes.get(so) || 0) + vol);
      }

      // Pass 1: Bucket rows by the dominant order
      for (let i = 0; i < flatData.length; i++) {
        const item = flatData[i];
        const bo = String(item['BO No'] ?? '');
        const so = String(item['SO No'] ?? '');
        const volBo = boVolumes.get(bo) || 0;
        const volSo = soVolumes.get(so) || 0;

        const dominantId = volBo > volSo ? `BO-${bo}` : (volSo > volBo ? `SO-${so}` : `BO-${bo}`);
        const key = dominantId;

        let bucket = buckets.get(key);
        if (!bucket) {
          bucket = [];
          buckets.set(key, bucket);
        }
        bucket.push(item);
      }
    } else {
      // Standard Pass 1: bucket rows by merge key
      for (let i = 0; i < flatData.length; i++) {
        const item = flatData[i];
        const key = mergeFields.map(f => String(item[f] ?? '')).join('|||');
        let bucket = buckets.get(key);
        if (!bucket) {
          bucket = [];
          buckets.set(key, bucket);
        }
        bucket.push(item);
      }
    }

    // Pass 2: resolve each bucket into one row
    // Compute nonAgreementFields once — Object.keys is identical for every bucket
    // since all rows come from the same dataset schema.
    const sampleKeys = flatData.length > 0 ? Object.keys(flatData[0] || {}) : [];
    const nonAgreementFields = isBSDominant
      ? sampleKeys.filter(f => !SUM_FIELDS.has(f) && (!mergeFields.includes(f) || f === 'BO No' || f === 'SO No'))
      : sampleKeys.filter(f => !SUM_FIELDS.has(f) && !mergeFields.includes(f));

    const result: any[] = [];
    for (const [, items] of buckets) {
      if (items.length === 1) {
        const single = { ...items[0] };
        // Harmonize empty order numbers so they render identically in pivot rowspan
        if (single['BO No'] === 0 || single['BO No'] === '0') single['BO No'] = '';
        if (single['SO No'] === 0 || single['SO No'] === '0') single['SO No'] = '';
        if (single['BUYER'] === undefined || single['BUYER'] === null) single['BUYER'] = '';
        if (single['SELLER'] === undefined || single['SELLER'] === null) single['SELLER'] = '';
        
        const boVal = Number(single['BO No']) || 0;
        const soVal = Number(single['SO No']) || 0;
        single._effectiveOrder = Math.min(boVal > 0 ? boVal : Infinity, soVal > 0 ? soVal : Infinity);
        
        result.push(single);
        continue;
      }

      const merged: any = { ...items[0] };

      // Sum numeric fields
      for (const f of SUM_FIELDS) {
        let sum = 0;
        for (let i = 0; i < items.length; i++) {
          sum += Number(items[i][f]) || 0;
        }
        merged[f] = sum;
      }

      // For every other non-key field: keep value only if all rows agree; otherwise blank it
      // If B/S dominance is active, BO No and SO No must also be evaluated for agreement because the subordinate one will differ

      for (const f of nonAgreementFields) {
        if (f === 'TIME') {
          let maxVal = '';
          for (let i = 0; i < items.length; i++) {
            const cur = String(items[i][f] ?? '');
            if (cur > maxVal) maxVal = cur;
          }
          merged[f] = maxVal;
          continue;
        }

        if (f === 'HAKA HAKI') {
          const firstBo = Number(items[0]['BO No']) || 0;
          const firstSo = Number(items[0]['SO No']) || 0;
          if (firstBo > 0 && firstSo > 0) {
            merged[f] = firstBo > firstSo ? 'HAKA' : 'HAKI';
          } else {
            merged[f] = items[0][f];
          }
          continue;
        }

        const first = items[0][f];
        const firstStr = String(first ?? '');
        let allSame = true;
        for (let i = 1; i < items.length; i++) {
          if (String(items[i][f] ?? '') !== firstStr) {
            allSame = false;
            break;
          }
        }
        merged[f] = allSame ? first : '';
      }

      merged._rawItems = items;

      if (isBSDominant) {
        // Restore the anchor ID for the dominant side, just in case they were blanked out during non-agreement checks
        const bo = String(items[0]['BO No'] ?? '');
        const so = String(items[0]['SO No'] ?? '');
        const volBo = boVolumes.get(bo) || 0;
        const volSo = soVolumes.get(so) || 0;
        if (volBo > volSo || (volBo === volSo && volBo > 0)) {
          merged['BO No'] = bo === '0' ? '' : bo;
        } else {
          merged['SO No'] = so === '0' ? '' : so;
        }
      }

      let minBo = Infinity;
      let minSo = Infinity;
      for (let i = 0; i < items.length; i++) {
        const boVal = Number(items[i]['BO No']) || 0;
        const soVal = Number(items[i]['SO No']) || 0;
        if (boVal > 0 && boVal < minBo) minBo = boVal;
        if (soVal > 0 && soVal < minSo) minSo = soVal;
      }
      merged._effectiveOrder = Math.min(minBo, minSo);

      result.push(merged);
    }

    if (groupActive) {
      result.sort((a, b) => a._effectiveOrder - b._effectiveOrder);
    }

    return result;
  }, [flatData, groupActive, mergeFields]);

  // Pre-calculate minimum Order Numbers for sorting labels
  const minOrders = useMemo(() => {
    const buyerMins: Record<string, number> = {};
    const sellerMins: Record<string, number> = {};
    
    for (let i = 0; i < flatData.length; i++) {
      const item = flatData[i];
      const boNo = Number(item['BO No']) || 0;
      const soNo = Number(item['SO No']) || 0;
      const effective = Math.min(
        boNo > 0 ? boNo : Infinity,
        soNo > 0 ? soNo : Infinity
      );
      if (effective === Infinity) continue;
      
      const buyer = item['BUYER'];
      if (buyer) {
        const cur = buyerMins[buyer];
        if (cur === undefined || effective < cur) {
          buyerMins[buyer] = effective;
        }
      }
      
      const seller = item['SELLER'];
      if (seller) {
        const cur = sellerMins[seller];
        if (cur === undefined || effective < cur) {
          sellerMins[seller] = effective;
        }
      }
    }
    return { buyerMinOrder: buyerMins, sellerMinOrder: sellerMins };
  }, [flatData]);

  const { buyerMinOrder, sellerMinOrder } = minOrders;

  // Pre-build lookup map for merged children
  const mergedChildrenLookup = useMemo(() => {
    const lookup = new Map<string, any[]>();
    const rowAttrs = pivotState.rows || [];
    
    for (let idx = 0; idx < mergedData.length; idx++) {
      const item = mergedData[idx];
      const keyStr = rowAttrs.map((attr: string) => {
        const val = item[attr];
        return val === 0 ? '0' : String(val ?? '');
      }).join('\0');

      if (!item._rawItems || item._rawItems.length <= 1) {
        continue;
      }

      const items = item._rawItems;
      
      // Highly optimized tracking loop to avoid Set/Map instantiation & array mapping
      let uniqueBoCount = 0;
      let uniqueSoCount = 0;
      const seenBo: Record<string, boolean> = {};
      const seenSo: Record<string, boolean> = {};
      let totalBoVol = 0;
      let totalSoVol = 0;

      for (let i = 0; i < items.length; i++) {
        const raw = items[i];
        const bo = String(raw['BO No'] ?? '');
        const so = String(raw['SO No'] ?? '');
        const vol = Number(raw['VOLUME']) || 0;

        if (bo && bo !== '0') {
          if (!seenBo[bo]) {
            seenBo[bo] = true;
            uniqueBoCount++;
          }
          totalBoVol += vol;
        }
        if (so && so !== '0') {
          if (!seenSo[so]) {
            seenSo[so] = true;
            uniqueSoCount++;
          }
          totalSoVol += vol;
        }
      }

      let sortByBo = true;
      if (uniqueBoCount !== uniqueSoCount) {
        sortByBo = uniqueBoCount > uniqueSoCount;
      } else {
        sortByBo = totalBoVol >= totalSoVol;
      }

      const sorted = [...items].sort((a, b) => {
        if (sortByBo) {
          const aNo = Number(a['BO No']) || Infinity;
          const bNo = Number(b['BO No']) || Infinity;
          return aNo - bNo;
        } else {
          const aNo = Number(a['SO No']) || Infinity;
          const bNo = Number(b['SO No']) || Infinity;
          return aNo - bNo;
        }
      });

      lookup.set(keyStr, sorted);
    }
    
    return lookup;
  }, [mergedData, pivotState.rows]);



  // Handlers
  const handleStockSelect = (stock: string) => {
    setSelectedStock(stock);
    setStockInput(stock);
    // Don't close dropdown - user clicks outside to close
  };

  const applyFilters = () => {
    if (!targetDate) {
      showToast({ type: 'warning', title: 'Warning', message: 'Please select a date' });
      return;
    }
    const d = new Date(targetDate);
    if (isNaN(d.getTime())) {
      showToast({ type: 'error', title: 'Error', message: 'Invalid date selected' });
      return;
    }

    if (startTime > endTime) {
      showToast({
        type: 'error',
        title: 'Batas Waktu Invalid',
        message: 'Jam awal tidak bisa lebih besar dari jam akhir'
      });
      return;
    }

    // Commit all draft states to applied states
    if (stockInput !== appliedStock || targetDate !== appliedDate) {
      setIsLoading(true);
      setIsFullDataLoading(true);
      setDoneDetailData(new Map());
    }
    setAppliedStock(stockInput);
    setAppliedDate(targetDate);
    setAppliedStartTime(startTime);
    setAppliedEndTime(endTime);
    setAppliedPrice(priceFilter);
    setAppliedBrokers(selectedBrokers);
    setAppliedBoard(selectedBoard);
    setAppliedInvType(selectedInvType);

    // setSelectedStock should also be updated to match stockInput if we want suggestions/logic to stay synced
    setSelectedStock(stockInput);
  };

  const [prevClose, setPrevClose] = useState<number | null | undefined>(undefined);
  const [sortDirs, setSortDirs] = useState<Record<string, 'asc' | 'desc' | 'none'>>({
    PRICE: 'desc',
    TIME: 'asc',
    'SO No': 'asc',
    'BO No': 'asc',
  });

  // Click outside listeners
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setShowStockSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);


  const filteredStocks = searchStocks(stockInput);

  return (
    <div className="w-full h-full flex flex-col bg-[#0a0f20]">
      {/* Top Controls - Wrapping Horizontal Ribbon */}
      <div className={`bg-card border-b border-[#3a4252] px-3 py-1.5 sticky top-0 z-[5000] transition-all duration-300`}>
        <div className="flex flex-row flex-wrap items-center gap-3 w-full">

          {/* Ticker Selection (hidden in dashboard mode) */}
          {!isDashboard && (
          <div className="flex items-center gap-2">
            <label className="text-[13px] font-medium text-white whitespace-nowrap">Ticker:</label>
            <div className="relative" ref={dropdownRef}>
              <div className="w-32 h-9 px-3 flex items-center justify-between bg-background border border-[#3a4252] rounded-md text-sm text-white hover:border-primary/50 focus-within:border-primary/50 transition-colors">
                <Search className="w-3.5 h-3.5 text-muted-foreground mr-2 flex-shrink-0" />
                <input
                  type="text"
                  className={`w-full bg-transparent text-sm text-white focus:outline-none placeholder:text-muted-foreground/50 truncate ${propSelectedStock ? 'cursor-not-allowed opacity-80' : ''}`}
                  placeholder="Select ticker"
                  value={stockInput}
                  onChange={(e) => {
                    if (propSelectedStock) return;
                    setStockInput(e.target.value.toUpperCase());
                    setShowStockSuggestions(true);
                  }}
                  onFocus={() => {
                    if (!propSelectedStock) setShowStockSuggestions(true);
                  }}
                  readOnly={!!propSelectedStock}
                />
                {!propSelectedStock && (
                  <ChevronDown className="w-3 h-3 text-muted-foreground opacity-50 ml-1 flex-shrink-0 cursor-pointer" onClick={() => setShowStockSuggestions(!showStockSuggestions)} />
                )}
              </div>
              {showStockSuggestions && (
                <div id="stock-suggestions" role="listbox" className="absolute top-full left-0 mt-1 bg-popover border border-[#3a4252] rounded-md shadow-lg z-[60] max-h-96 w-full overflow-hidden flex flex-col">
                  {/* Search Input (Removed) */}
                  <div className="overflow-y-auto flex-1">
                    {stockInput === '' ? (
                      <>
                        <div className="px-3 py-[2.06px] text-xs text-muted-foreground border-b border-[#3a4252] sticky top-0 bg-popover">
                          Available Stocks ({STOCK_LIST.length})
                        </div>
                        {STOCK_LIST.slice(0, 20).map((stock) => {
                          const isSelected = stock === selectedStock;
                          return (
                            <div
                              key={stock}
                              onClick={() => handleStockSelect(stock)}
                              className={`px-3 py-[2.06px] cursor-pointer text-sm flex justify-between items-center ${isSelected ? 'text-primary font-medium bg-accent/50' : 'hover:bg-muted'}`}
                            >
                              <span>{stock}</span>
                              {isSelected && <span className="text-primary text-xs">✓</span>}
                            </div>
                          );
                        })}
                        {STOCK_LIST.length > 20 && (
                          <div className="px-3 py-[2.06px] text-xs text-muted-foreground border-t border-[#3a4252]">
                            ... and {STOCK_LIST.length - 20} more stocks
                          </div>
                        )}
                      </>
                    ) : filteredStocks.length > 0 ? (
                      <>
                        <div className="px-3 py-[2.06px] text-xs text-muted-foreground border-b border-[#3a4252] sticky top-0 bg-popover">
                          {filteredStocks.length} stocks found
                        </div>
                        {filteredStocks.slice(0, 20).map((stock) => {
                          const isSelected = stock === selectedStock;
                          return (
                            <div
                              key={stock}
                              onClick={() => handleStockSelect(stock)}
                              className={`px-3 py-[2.06px] cursor-pointer text-sm flex justify-between items-center ${isSelected ? 'text-primary font-medium bg-accent/50' : 'hover:bg-muted'}`}
                            >
                              <span>{stock}</span>
                              {isSelected && <span className="text-primary text-xs">✓</span>}
                            </div>
                          );
                        })}
                        {filteredStocks.length > 20 && (
                          <div className="px-3 py-[2.06px] text-xs text-muted-foreground border-t border-[#3a4252]">
                            ... and {filteredStocks.length - 20} more stocks
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="px-3 py-[2.06px] text-xs text-muted-foreground border-b border-[#3a4252] sticky top-0 bg-popover">
                        Stocks (0)
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          )}

          {/* Date Picker */}
          <div className="flex items-center gap-2">
            <label className="text-[13px] font-medium text-white whitespace-nowrap">Date:</label>
            <div className="relative h-9 w-[140px] border border-[#3a4252] rounded-md overflow-hidden focus-within:ring-1 focus-within:ring-primary/50">
              <Calendar
                className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-white/70 cursor-pointer z-[2]"
                onClick={() => (dateRef.current as any)?.showPicker?.()}
              />
              <input
                ref={dateRef}
                type="date"
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
                onClick={() => (dateRef.current as any)?.showPicker?.()}
                className="no-calendar-indicator absolute top-0 left-0 h-full w-[170px] pl-2 bg-background text-sm text-white focus:outline-none cursor-pointer border-0"
              />
            </div>
          </div>

          {/* Time Range */}
          <div className="flex items-center gap-1.5">
            <label className="text-[13px] font-medium text-white whitespace-nowrap">Time:</label>
            <div className="flex items-center gap-1">
              <input
                type="time"
                step="1"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="h-9 px-2 bg-background border border-[#3a4252] rounded-md text-xs text-white focus:outline-none focus:ring-1 focus:ring-primary/50 [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-70 [&::-webkit-calendar-picker-indicator]:cursor-pointer"
              />
              <span className="text-white/40 text-xs">-</span>
              <input
                type="time"
                step="1"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="h-9 px-2 bg-background border border-[#3a4252] rounded-md text-xs text-white focus:outline-none focus:ring-1 focus:ring-primary/50 [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-70 [&::-webkit-calendar-picker-indicator]:cursor-pointer"
              />
            </div>
          </div>

          {/* Price Input — disabled for trial users */}
          <div className="flex items-center gap-2">
            <label className="text-[13px] font-medium text-white whitespace-nowrap">Price:</label>
            <div 
              className="relative"
              onClick={() => {
                if (isTrial) {
                  showToast({
                    type: 'warning',
                    title: 'Fitur Terbatas',
                    message: 'Dalam masa free trial, fitur filter harga tidak tersedia.'
                  });
                }
              }}
            >
              <input
                type="text"
                value={priceFilter}
                onChange={(e) => {
                  if (isTrial) return;
                  setPriceFilter(e.target.value);
                }}
                onFocus={() => {
                  if (isTrial) {
                    showToast({
                      type: 'warning',
                      title: 'Fitur Terbatas',
                      message: 'Dalam masa free trial, fitur filter harga tidak tersedia.'
                    });
                  }
                }}
                readOnly={isTrial}
                className={`w-20 h-9 px-3 pr-7 bg-background border border-[#3a4252] rounded-md text-sm text-white focus:outline-none focus:ring-1 focus:ring-primary/50 placeholder:text-muted-foreground/50 ${isTrial ? 'cursor-not-allowed opacity-80' : ''}`}
                placeholder="All"
              />
              {priceFilter && !isTrial && (
                <button
                  onClick={() => setPriceFilter('')}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-muted-foreground hover:text-white transition-colors"
                  tabIndex={-1}
                >
                  ×
                </button>
              )}
            </div>
          </div>

          {/* Broker Input — disabled for trial users */}
          <div className="flex items-center gap-2">
            <label className="text-[13px] font-medium text-white whitespace-nowrap">Broker:</label>
            <BrokerSelector
              selectedBrokers={selectedBrokers}
              setSelectedBrokers={setSelectedBrokers}
              availableBrokers={availableBrokers}
              isTrial={isTrial}
              showToast={showToast}
            />
          </div>

          {/* Board Dropdown */}
          <div className="flex items-center gap-2">
            <label className="text-[13px] font-medium text-white whitespace-nowrap">Board:</label>
            <div className="relative">
              <select
                value={selectedBoard}
                onChange={(e) => setSelectedBoard(e.target.value)}
                className="h-9 min-w-[60px] pl-3 pr-8 bg-background border border-[#3a4252] rounded-md text-sm text-white outline-none cursor-pointer appearance-none"
              >
                <option value="All">All</option>
                <option value="RG">RG</option>
                <option value="TN">TN</option>
                <option value="NG">NG</option>
              </select>
              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            </div>
          </div>

          {/* F/D Dropdown */}
          <div className="flex items-center gap-2">
            <label className="text-[13px] font-medium text-white whitespace-nowrap">F/D:</label>
            <div className="relative">
              <select
                value={selectedInvType}
                onChange={(e) => setSelectedInvType(e.target.value)}
                className="h-9 min-w-[60px] pl-3 pr-8 bg-background border border-[#3a4252] rounded-md text-sm text-white outline-none cursor-pointer appearance-none"
              >
                <option value="All">All</option>
                <option value="Foreign">Foreign</option>
                <option value="Domestic">Domestic</option>
              </select>
              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            </div>
          </div>

          {/* Show Button */}
          <Button
            onClick={applyFilters}
            className="h-[34px] px-4 min-w-[72px] bg-white text-black hover:bg-gray-200 font-bold text-sm transition-colors ml-2 flex items-center justify-center"
            disabled={isLoading || isFullDataLoading}
          >
            {(isLoading || isFullDataLoading) ? <Loader2 className="h-4 w-4 animate-spin text-black" /> : 'Show'}
          </Button>

          <div className="flex-1" />
        </div>
      </div>



      {/* Main Content: Pivot Table */}
      <div ref={pivotAreaRef} className={`flex-1 min-h-0 overflow-x-auto overflow-y-hidden dark-pivot-theme${groupActive ? ' group-locked' : ''}`}>
        {mergedData.length > 0 ? (
          <PivotTableUI
            {...pivotState}
            data={mergedData}
            tableOptions={{
              sortByOrderNumber: groupActive,
              groupActive: groupActive,
              onExpandedRowsChange: setHasExpandedRows,
              getMergedChildren: (rowKey: string[], rowAttrs: string[]) => {
                const keyStr = rowAttrs.map((_, i) => rowKey[i] === '0' ? '0' : String(rowKey[i] ?? '')).join('\0');
                return mergedChildrenLookup.get(keyStr) || null;
              }
            }}
            activeMergeFields={mergeFields}
            onToggleMergeField={() => { }}
            sortDirs={sortDirs}
            prevClose={prevClose}
            hiddenFromDragDrop={['VOLUME', 'TYP', 'STK_CODE', 'VALUE']}
            rendererCellChild={
              (() => {
                // Single handler used by both button variants
                const handleGroupToggle = () => {
                  const turningOn = !groupActive;
                  // Batch all state updates in startTransition so the heavy
                  // mergedData recompute is deferred and doesn't block the UI.
                  startTransition(() => {
                    if (turningOn) {
                      preGroupRowsRef.current = pivotState.rows || [];
                      setPivotState((prev: any) => ({
                        ...prev,
                        rows: LOCKED_GROUP_ROWS,
                        cols: [],
                      }));
                    } else {
                      const savedRows = preGroupRowsRef.current.length > 0
                        ? preGroupRowsRef.current
                        : ['BUYER', 'PRICE', 'SELLER', 'TIME'];
                      setPivotState((prev: any) => ({
                        ...prev,
                        rows: savedRows,
                      }));
                      window.dispatchEvent(new Event('collapseAllRows'));
                    }
                    // Both state updates in the same transition = single render
                    setGroupActive(turningOn);
                  });
                };

                return (groupActive && hasExpandedRows) ? (
                  <div className="flex flex-col w-full h-[40px] min-h-[40px]">
                    <button
                      onClick={handleGroupToggle}
                      className={`w-full flex-1 h-[20px] min-h-[20px] px-2 flex items-center justify-center gap-1.5 transition-colors border-b border-[#3a4252]/50 ${groupActive
                        ? 'bg-blue-600 text-white'
                        : 'bg-transparent text-white hover:bg-white/10'
                        }`}
                    >
                      <span className="text-[11px] font-bold uppercase tracking-tight">
                        GROUP: {groupActive ? 'ON' : 'OFF'}
                      </span>
                    </button>
                    <button
                      onClick={() => window.dispatchEvent(new Event('collapseAllRows'))}
                      className="w-full flex-1 h-[20px] min-h-[20px] px-2 flex items-center justify-center gap-1.5 bg-transparent text-gray-400 hover:bg-white/10 hover:text-white transition-colors"
                    >
                      <span className="text-[10px] uppercase tracking-tight">Collapse All</span>
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col w-full h-[40px] min-h-[40px]">
                    <button
                      onClick={handleGroupToggle}
                      className={`w-full h-full px-2 flex items-center justify-center gap-1.5 transition-colors ${groupActive
                        ? 'bg-blue-600 text-white'
                        : 'bg-transparent text-white hover:bg-white/10'
                        }`}
                    >
                      <span className="text-[11px] font-bold uppercase tracking-tight">
                        GROUP: {groupActive ? 'ON' : 'OFF'}
                      </span>
                    </button>
                  </div>
                );
              })()
            }
            onChange={(s: any) => {
              if (s.sortDirs) {
                setSortDirs(s.sortDirs);
              }
              // When group is active, always enforce locked row order
              if (groupActive) {
                setPivotState({
                  ...s,
                  rows: LOCKED_GROUP_ROWS,
                  cols: [],
                });
              } else {
                setPivotState(s);
              }
            }}
            renderers={(() => {
              // Robust normalization for ESM interop
              const unwrap = (obj: any) => {
                if (!obj) return {};
                // Handle ESM default export
                if (obj.__esModule && obj.default) return obj.default;
                // Handle nested default
                if (typeof obj === 'object' && obj.default && typeof obj.default === 'object') {
                  return obj.default;
                }
                return obj;
              };

              const tableRend = unwrap(TableRenderers);
              return { Table: tableRend.Table };
            })()}
            sorters={{
              ...customSorters,
              ...pivotState.sorters,
              BUYER: (a: any, b: any) => {
                const valA = buyerMinOrder[a] || 999999999;
                const valB = buyerMinOrder[b] || 999999999;
                if (valA !== valB) return valA - valB;
                return String(a).localeCompare(String(b));
              },
              SELLER: (a: any, b: any) => {
                const valA = sellerMinOrder[a] || 999999999;
                const valB = sellerMinOrder[b] || 999999999;
                if (valA !== valB) return valA - valB;
                return String(a).localeCompare(String(b));
              },
              'HAKA HAKI': (a: any, b: any) => {
                if (a === 'HAKI' && b === 'HAKA') return -1;
                if (a === 'HAKA' && b === 'HAKI') return 1;
                return 0;
              }
            }}
            aggregators={filteredAggregators}
            hiddenAttributes={['SESSION', 'DATE', 'INV_TYP1', 'INV_TYP2', '_rawItems']}
            unusedOrientationCutoff={Infinity} // Prevent unused fields from being hidden too aggressively
            menuLimit={100000}
          />
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-muted-foreground">
            {isLoading ? (
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="h-10 w-10 animate-spin text-primary/50" />
                <p className="text-lg font-medium text-foreground">Loading transaction data...</p>
                <p className="text-sm">This may take a moment for large datasets.</p>
              </div>
            ) : (
              <>
                <Search className="h-16 w-16 mb-4 opacity-20" />
                <p className="text-lg font-medium">No data to display</p>
                <p className="text-sm opacity-70">Select a stock and date to view transaction details</p>
              </>
            )}
          </div>
        )}
      </div>

      <style>{`
        /* Minimal Dark Mode Overrides for React Pivottable */
        .dark-pivot-theme .pvtUi {
            color: #e2e8f0;
            width: 100%;
            border: none !important;
            background-color: transparent !important;
            border-collapse: collapse !important;
            margin: 0 !important;
            padding: 0 !important;
        }

        .dark-pivot-theme .pvtOutput {
            vertical-align: top !important;
            padding: 0 !important;
        }
        .dark-pivot-theme .pvtOutput > div {
            padding: 0 !important;
        }
        .dark-pivot-theme .pvtTable {
            border-collapse: collapse !important;
            width: auto;
        }

        .dark-pivot-theme .pvtTable th,
        .dark-pivot-theme .pvtTable td {
            padding: 2px 8px !important;
            font-size: 9px !important;
            height: auto !important;
            line-height: 1 !important;
        }
        /* Sticky Pivot UI Header (the attribute buttons) */
        .dark-pivot-theme .pvtTable thead tr:first-child .pvtUi,
        .dark-pivot-theme .pvtUi {
            position: sticky !important;
            top: 0 !important;
            z-index: 500 !important;
            background-color: #0d1425 !important;
        }
            /* Ultra-Visible Grid and Borders for the entire Pivot Table */
        .dark-pivot-theme .pvtTable {
            border: 1px solid #4b5563 !important;
            border-collapse: collapse !important;
            table-layout: auto !important;
        }

        .dark-pivot-theme .pvtTable thead th,
        .dark-pivot-theme .pvtTable tbody tr th,
        .dark-pivot-theme .pvtTable tbody tr td,
        .dark-pivot-theme .pvtTable .pvtRowLabel,
        .dark-pivot-theme .pvtTable .pvtColLabel,
        .dark-pivot-theme .pvtTable .pvtAxisLabel,
        .dark-pivot-theme .pvtTable .pvtCornerLabel,
        .dark-pivot-theme .pvtTable .pvtTotalLabel {
            border: 1px solid #4b5563 !important; /* Clearly visible slate-grey border */
            background-color: transparent; /* Background is handled by specific rules */
            box-sizing: border-box !important;
        }

        .dark-pivot-theme .pvtTable tbody tr td,
        .dark-pivot-theme .pvtTable tbody tr th {
            border-top: none !important;
            border-bottom: none !important;
        }


        /* Comprehensive Header Borders and Grid styling */
        .dark-pivot-theme .pvtTable {
            border-collapse: collapse !important;
            table-layout: auto !important;
        }

        .dark-pivot-theme .pvtTable thead th {
            background-color: #111827 !important;
            padding: 4px 8px !important;
            line-height: normal !important;
            box-sizing: border-box !important;
        }

        /* Lock the entire thead as a single unit for vertical scroll */
        .dark-pivot-theme .pvtTable thead {
            position: sticky !important;
            top: 0 !important;
            z-index: 100 !important;
            background-color: #111827 !important;
        }

            line-height: normal !important;
        }

        /* Essential Horizontal Locking - Row Labels */
        .dark-pivot-theme .pvtTable thead th.pvtRowAxisLabel,
        .dark-pivot-theme .pvtTable thead th.pvtCornerLabel {
            position: sticky !important;
            left: 0 !important;
            z-index: 150 !important;
            border: 1px solid #4b5563 !important;
        }

        .dark-pivot-theme .pvtTable tbody .pvtRowLabel {
            position: sticky !important;
            left: 0 !important;
            z-index: 50 !important;
            background-color: #111827 !important;
        }

        /* Pivot UI Buttons - also sticky above the table */
        .dark-pivot-theme .pvtUi {
            position: sticky !important;
            top: 0 !important;
            z-index: 500 !important;
            background-color: #0d1425 !important;
            padding-bottom: 5px !important;
        }

        /* Adjust table top to be below the UI if needed, 
           but since both are in the same scroll container, 
           thead top: 0 should behave correctly. */
        /* Sticky Totals Row (Footer) */
        .dark-pivot-theme .pvtTable tbody tr.pvtTotalRow th,
        .dark-pivot-theme .pvtTable tbody tr.pvtTotalRow td {
            position: sticky !important;
            bottom: 0 !important;
            z-index: 110 !important; /* Slightly higher than header to stay on top */
            background-color: #0d1425 !important;
            border: 1px solid #3a4252 !important;
        }

        /* Sticky Row Labels (Left Columns) */
        .dark-pivot-theme .pvtTable .pvtRowLabel {
            position: sticky !important;
            z-index: 50 !important;
            background-color: #111827 !important;
            border: 1px solid #3a4252 !important;
        }

        /* Sticky Header Rows - stable multi-level locking with tight offsets */
        .dark-pivot-theme .pvtTable thead th {
            position: sticky !important;
            background-color: #111827 !important;
            border: 1px solid #3a4252 !important;
            z-index: 100 !important;
            padding: 2px 4px !important; /* Extremely tight padding */
            height: 24px !important;
            line-height: normal !important;
        }

        .dark-pivot-theme .pvtTable thead tr:nth-child(1) th { top: 0 !important; z-index: 110 !important; }
        .dark-pivot-theme .pvtTable thead tr:nth-child(2) th { top: 24px !important; z-index: 109 !important; }
        .dark-pivot-theme .pvtTable thead tr:nth-child(3) th { top: 48px !important; z-index: 108 !important; }
        .dark-pivot-theme .pvtTable thead tr:nth-child(4) th { top: 72px !important; z-index: 107 !important; }
        .dark-pivot-theme .pvtTable thead tr:nth-child(5) th { top: 96px !important; z-index: 106 !important; }

        /* Essential Axis Locks */
        .dark-pivot-theme .pvtTable thead th.pvtRowAxisLabel,
        .dark-pivot-theme .pvtTable thead th.pvtCornerLabel {
            left: 0 !important;
            z-index: 150 !important;
        }

        /* Dummy Row for Scrollbar Space */
        .dark-pivot-theme .pvtDummyRow {
            height: 16px !important;
            border: none !important;
            background-color: transparent !important;
        }
        .dark-pivot-theme .pvtDummyRow td {
            height: 16px !important;
            border: none !important;
            background-color: transparent !important;
            padding: 0 !important;
        }

        .dark-pivot-theme .pvtAttr {
            background: #1e293b !important;
            color: #f8fafc !important;
            border: 1px solid #3a4252 !important;
            border-radius: 4px;
            padding: 4px 10px;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            height: 32px !important; /* Standard height for attributes */
            min-width: fit-content !important; /* Allow width based on content */
            white-space: nowrap !important;
            font-size: 0.75rem !important;
            margin: 2px !important;
            cursor: move;
            flex-shrink: 0 !important; /* Prevent squeezing in flex containers */
            position: relative !important; /* Anchor for the absolute filter box */
        }
        .dark-pivot-theme .pvtColAxisLabel {
            display: none !important;
        }

        .dark-pivot-theme .pvtRowAxisLabel,
        .dark-pivot-theme .pvtTotalLabel {
            text-align: center !important;
            font-weight: bold !important;
        }

        .dark-pivot-theme .pvtTriangle {
            color: #94a3b8 !important;
            font-size: 18px !important;
            margin-left: 5px;
        }
        
        /* Custom Dropdowns (.pvtDropdown) matching ribbon styling */
        .dark-pivot-theme .pvtDropdown {
            background-color: #161d31 !important;
            border: 1px solid #3a4252 !important;
            border-radius: 4px !important;
            height: 36px !important;
            display: flex !important;
            align-items: center !important;
            color: #ffffff !important;
            cursor: pointer;
            position: relative;
            margin: 0 !important; /* Unified margin */
            min-width: 80px;
            width: auto;
            position: relative !important;
        }

        .dark-pivot-theme .pvtDropdownCurrent {
            background-color: transparent !important;
            color: #ffffff !important;
            padding: 0 30px 0 12px !important;
            display: flex !important;
            align-items: center !important;
            height: 100% !important;
            border: none !important; /* Removed redundant border */
            width: 100% !important;
            text-align: left !important;
        }

        /* Dropdown Icon / Arrow - Ensure right positioning */
        .dark-pivot-theme .pvtDropdownIcon {
            color: #94a3b8 !important;
            float: right !important;
            margin-right: 12px !important;
            pointer-events: none;
            position: absolute !important;
            right: 0 !important;
            top: 50% !important;
            transform: translateY(-50%) !important;
            font-size: 18px !important;
        }

        /* Ensure Renderer dropdown has a border container if needed */
        .dark-pivot-theme .pvtRenderers {
            border: none !important; /* Removed to avoid double border with dropdown */
            padding: 0 !important; /* Remove padding to align dropdown perfectly */
            background-color: transparent !important;
            text-align: left !important;
        }

        /* Dropdown Menu (The expanded part) */
        .dark-pivot-theme .pvtDropdownMenu {
            background-color: #0a0f20 !important;
            border: 1px solid #3a4252 !important;
            border-radius: 0.375rem !important;
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.5) !important;
            padding: 4px 0 !important;
            z-index: 3000 !important; /* Much higher to clear attributes and other triggers */
            position: absolute !important;
            top: 100% !important; /* Anchor exactly below the trigger */
            left: 0 !important;
            margin-top: 4px !important; /* Small gap for cleaner look */
            min-width: 200px !important;
            max-height: 400px !important;
            overflow-y: auto !important;
        }

        /* Dropdown Values (Items) */
        .dark-pivot-theme .pvtDropdownValue {
            color: #ffffff !important;
            padding: 6px 12px !important;
            font-size: 0.875rem !important;
            background-color: transparent !important;
        }

        .dark-pivot-theme .pvtDropdownActiveValue {
            background-color: #1e293b !important;
            color: #ffffff !important;
        }

        .dark-pivot-theme .pvtDropdownValue:hover {
            background-color: #1e293b !important;
        }

        /* Search input inside dropdown if any */
        .dark-pivot-theme .pvtDropdownMenu input {
            background-color: #161d31 !important;
            border: 1px solid #3a4252 !important;
            color: #ffffff !important;
            margin: 4px 8px !important;
            width: calc(100% - 16px) !important;
            border-radius: 4px !important;
            padding: 4px 8px !important;
        }

        /* Cleanup previous styles for selects to be consistent */
        .dark-pivot-theme select.pvtAggregator,
        .dark-pivot-theme select.pvtRenderer,
        .dark-pivot-theme select.pvtAttrDropdown {
            background-color: #0a0f20 !important;
            color: #ffffff !important;
            border: 1px solid #3a4252 !important;
            border-radius: 0.375rem !important;
            height: 36px !important;
            padding: 0 10px !important;
            outline: none;
            color-scheme: dark;
        }

        .dark-pivot-theme .pvtAxisContainer, .dark-pivot-theme .pvtVals {
            background-color: transparent !important;
            border: 1px solid #3a4252 !important;
            /* Prevent axes from clipping dropdowns */
            overflow: visible !important;
            padding: 4px !important; /* Consistent padding */
            min-width: 80px !important;
            text-align: left !important;
        }

        .dark-pivot-theme td.pvtAxisContainer.pvtUnused,
        .dark-pivot-theme .pvtAxisContainer.pvtUnused {
            border-bottom: none !important;
            border-right: none !important;
        }

        .dark-pivot-theme .pvtVals {
            border: none !important; /* Remove border from Vals cell as triggers have them */
            padding: 0 !important;
        }

        /* Aggressively hide the orientation arrows (↓ and →) and any sorting icons */
        .dark-pivot-theme .pvtRowOrder,
        .dark-pivot-theme .pvtColOrder,
        .dark-pivot-theme .pvtVals a.pvtRowOrder,
        .dark-pivot-theme .pvtVals a.pvtColOrder,
        .dark-pivot-theme a.pvtRowOrder,
        .dark-pivot-theme a.pvtColOrder {
            display: none !important;
            visibility: hidden !important;
            opacity: 0 !important;
            pointer-events: none !important;
            width: 0 !important;
            height: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
            position: absolute !important;
        }

        .dark-pivot-theme .pvtAxisContainer br, .dark-pivot-theme .pvtVals br {
            display: none !important;
        }

        /* Placeholder text for empty drop zones */
        .dark-pivot-theme .pvtRows:empty:after,
        .dark-pivot-theme .pvtCols:empty:after {
            content: "Drag & Drop here";
            display: block;
            color: #4b5563;
            font-style: italic;
            font-size: 11px;
            padding: 10px;
            text-align: center;
            width: 100%;
        }

        /* For the case where technicality might have invisible content */
        .dark-pivot-theme .pvtRows:not(:has(.pvtAttr)):after,
        .dark-pivot-theme .pvtCols:not(:has(.pvtAttr)):after {
            content: "Drag & Drop here";
            display: block;
            color: #4b5563;
            font-style: italic;
            font-size: 11px;
            padding: 10px;
            text-align: center;
        }
        .dark-pivot-theme table.pvtTable {
            background-color: #0a0f20;
            color: #e2e8f0;
            border-collapse: collapse;
            font-size: 0.65rem;
            border: 1px solid #3a4252 !important;
            margin: 0 !important; /* Remove bottom margin */
        }
        .dark-pivot-theme .pvtTableContainer {
            border: none !important;
            padding: 0 !important;
            margin: 0 !important;
        }
        .dark-pivot-theme table.pvtTable tbody tr {
            height: 24px !important;
        }
        .dark-pivot-theme table.pvtTable tbody tr.pvtChildRow {
            height: 24px !important;
        }
        .dark-pivot-theme table.pvtTable tbody tr.pvtChildRow td {
            font-size: 9px !important;
            padding: 2px 6px !important;
            height: 24px !important;
        }
        .dark-pivot-theme table.pvtTable tbody tr.pvtChildRow td.pvtTotal {
            font-size: 0.7rem !important;
            padding-right: 8px !important;
        }
        .dark-pivot-theme table.pvtTable thead tr th, 
        .dark-pivot-theme table.pvtTable tbody tr th {
            background-color: #1e293b !important;
            color: #f8fafc;
            border: 1px solid #3a4252 !important;
            font-size: 0.6rem;
            padding: 4px 4px !important;
            height: 24px !important; /* Consistent row height across Group ON/OFF */
            text-align: center !important; /* Global header centering */
            vertical-align: middle !important;
        }
        .dark-pivot-theme table.pvtTable tbody tr th.bg-haka-haki {
            background-color: #4b5563 !important;
        }
        .dark-pivot-theme table.pvtTable tbody tr td {
            background-color: transparent !important;
            color: #e2e8f0;
            border: 1px solid #3a4252 !important;
            padding: 4px 8px !important;
            height: 24px !important; /* Consistent row height across Group ON/OFF */
            text-align: right !important; /* Global value right-alignment */
            vertical-align: middle !important;
        }
        .dark-pivot-theme .pvtTotal, 
        .dark-pivot-theme .pvtGrandTotal,
        .dark-pivot-theme .pvtTotalLabel {
            font-weight: bold !important;
            background-color: #1e293b !important;
            border: 1px solid #3a4252 !important;
            text-align: right !important;
            vertical-align: middle !important;
            white-space: nowrap !important;
            min-width: 50px !important;
            padding: 4px 8px !important;
            font-size: 0.6rem !important;
        }
        
        /* ===== GROUP: ON — Column header color coding =====
           Column order is locked: BO No(1) | SO No(2) | TIME(3) | PRICE(4) | BUYER(5) | SELLER(6) | Totals(7)
           BO No & SO No keep the existing gray. Others get distinct accent colors. */

        /* TIME, PRICE, BUYER, SELLER, and Totals — uniform soft muted amber/brown as requested */
        .group-locked table.pvtTable thead tr th:nth-child(3),
        .group-locked table.pvtTable thead tr th:nth-child(4),
        .group-locked table.pvtTable thead tr th:nth-child(5),
        .group-locked table.pvtTable thead tr th:nth-child(6),
        .group-locked table.pvtTable thead tr th:nth-child(7) {
            background-color: #3d3420 !important;
        }

        .group-locked table.pvtTable {
            table-layout: fixed !important;
            width: 440px !important;
        }

        /* ===== GROUP LOCKED STATE: disable drag & drop on rows/unused ===== */
        .dark-pivot-theme .pvtUnused {
            background: #111827 !important;
            border: 1px solid #3a4252 !important;
            border-bottom: none !important;
            border-right: none !important;
            border-radius: 4px;
            min-height: 28px !important;
            display: flex !important;
            flex-wrap: wrap !important; /* Allow fields to wrap vertically */
            overflow: visible !important; /* Prevent clipping of dropdowns */
            max-width: calc(100vw - ${sidebarOpen ? '256px' : '64px'}) !important; /* Flush against sidebar width (64/16 rem -> 256/64 px) */
            padding: 2px !important;
            align-items: center !important;
            scrollbar-width: none; /* No scrollbar needed if wrapping */
        }

        .dark-pivot-theme .pvtUnused::-webkit-scrollbar {
            height: 6px;
        }
        .dark-pivot-theme .pvtUnused::-webkit-scrollbar-track {
            background: #111827;
        }
        .dark-pivot-theme .pvtUnused::-webkit-scrollbar-thumb {
            background-color: #3a4252;
            border-radius: 10px;
        }

        .dark-pivot-theme .pvtRows, .dark-pivot-theme .pvtCols {
            background: #111827 !important;
            border: 1px solid #3a4252 !important;
            border-radius: 4px;
            min-height: 26px !important;
            overflow: visible !important; /* Allow filter box to show outside */
        }

        /* ===== GROUP LOCKED STATE: disable drag & drop on rows/unused ===== */
        /* Disable drag interactions in pvtRows, pvtCols and pvtUnused */
        .group-locked .pvtRows,
        .group-locked .pvtUnused,
        .group-locked .pvtCols {
            pointer-events: none !important;
            user-select: none !important;
        }

        /* Re-enable pointer events only for filter boxes (clicking the field badge) */
        .group-locked .pvtRows .pvtFilterBox,
        .group-locked .pvtRows .pvtAttr,
        .group-locked .pvtUnused .pvtFilterBox {
            pointer-events: auto !important;
        }

        /* In locked mode, attribute badges show default cursor (no grab hand) */
        .group-locked .pvtRows .pvtAttr {
            cursor: default !important;
        }

        /* Visual lock indicator on the pvtRows area */
        .group-locked .pvtRows {
            border: 1px solid #2563eb !important;
            background: #111827 !important;
            position: relative;
        }
        .group-locked .pvtRows::after {
            content: "🔒 Locked";
            position: absolute;
            top: -1px;
            right: 4px;
            font-size: 8px;
            color: #60a5fa;
            font-weight: bold;
            pointer-events: none;
            line-height: 1;
            padding: 1px 3px;
            background: #1e3a8a;
            border-radius: 2px;
        }

        /* Dim the unused area to signal it's not interactive */
        .group-locked .pvtUnused {
            opacity: 0.55 !important;
        }

        /* Cols area also locked when group active */
        .group-locked .pvtCols {
            opacity: 0.55 !important;
        }
        /* ================================================================= */
        
        /* Draggable fields scaling */
        .dark-pivot-theme .pvtAttr {
            padding: 2px 6px !important;
            font-size: 9.5px !important;
            margin: 1px !important;
        }

        /* Scale down the dropdowns on the left */
        .dark-pivot-theme select.pvtRenderer,
        .dark-pivot-theme select.pvtAggregator {
            font-size: 11px !important;
            padding: 2px 4px !important;
            height: 24px !important;
            background-color: #1e293b !important;
            color: #f8fafc !important;
            border: 1px solid #3a4252 !important;
            border-radius: 4px;
        }
        
        .dark-pivot-theme .pvtRenderers,
        .dark-pivot-theme .pvtVals {
            padding: 4px !important;
        }

        /* Hover states */
        .dark-pivot-theme .pvtAttr:hover {
            background: #334155 !important;
        }

        /* Colorize specific draggable attribute buttons */
        .dark-pivot-theme .pvtAttr[data-id="BUYER"],
        .dark-pivot-theme .pvtAttr[data-id="BO No"],
        .dark-pivot-theme .pvtAttr[data-id="F/D BUY"] {
            background-color: #065f46 !important; /* emerald-800 */
            border-color: #064e3b !important;
        }
        .dark-pivot-theme .pvtAttr[data-id="BUYER"]:hover,
        .dark-pivot-theme .pvtAttr[data-id="BO No"]:hover,
        .dark-pivot-theme .pvtAttr[data-id="F/D BUY"]:hover {
            background-color: #047857 !important; /* emerald-700 */
        }

        .dark-pivot-theme .pvtAttr[data-id="SELLER"],
        .dark-pivot-theme .pvtAttr[data-id="SO No"],
        .dark-pivot-theme .pvtAttr[data-id="F/D SELL"] {
            background-color: #991b1b !important; /* red-800 */
            border-color: #7f1d1d !important;
        }
        .dark-pivot-theme .pvtAttr[data-id="SELLER"]:hover,
        .dark-pivot-theme .pvtAttr[data-id="SO No"]:hover,
        .dark-pivot-theme .pvtAttr[data-id="F/D SELL"]:hover {
            background-color: #b91c1c !important; /* red-700 */
        }
        
        /* Tooltip/Dropdown styling if needed */
        .dark-pivot-theme .pvtFilterBox {
            background: #161d31 !important;
            border: 1px solid #3a4252 !important;
            z-index: 10000 !important;
            padding: 12px;
            border-radius: 8px;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.4) !important;
            top: 100% !important; /* Force open below */
            bottom: auto !important; /* Prevent opening upwards */
            left: 0 !important;
        }
        .dark-pivot-theme .pvtFilterBox input[type="text"] {
             background: #0a0f20;
             color: #fff;
             border: 1px solid #3a4252;
             padding: 6px 10px;
             border-radius: 4px;
             width: 100%;
             margin-bottom: 8px;
        }
        .dark-pivot-theme .pvtFilterBox h4 {
            background: transparent !important; 
            color: #fff !important;
            margin: 0 0 12px 0 !important;
            font-size: 1rem !important;
        }
        .dark-pivot-theme .pvtFilterBox .pvtButton {
            background: #283046 !important;
            color: #fff !important;
            border: 1px solid #3a4252 !important;
            padding: 4px 12px !important;
            border-radius: 4px !important; 
            cursor: pointer;
            font-size: 12px !important;
            display: inline-block !important;
            margin: 2px !important;
        }
        .dark-pivot-theme .pvtFilterBox .pvtButton:hover {
            background: #3a4252 !important;
        }

        /* Hide the drag handle (three lines) in filter box */
        .dark-pivot-theme .pvtDragHandle {
            display: none !important;
        }

        .dark-pivot-theme .pvtCheckContainer {
            background: #0a0f20 !important;
            border: 1px solid #3a4252 !important;
            border-radius: 4px;
            margin-top: 8px;
        }

        .dark-pivot-theme .pvtCheckContainer p {
            color: #cbd5e1 !important;
            padding: 4px 10px 4px 22px !important; /* Tighter padding */
            margin: 0 !important;
            cursor: pointer !important;
            position: relative !important;
            transition: background 0.2s ease;
            font-size: 13px !important;
        }

        /* Hide the default spacers and 'only' links to remove the gap */
        .dark-pivot-theme .pvtCheckContainer p .pvtOnly,
        .dark-pivot-theme .pvtCheckContainer p .pvtOnlySpacer {
            display: none !important;
        }

        /* Checkbox Base */
        .dark-pivot-theme .pvtCheckContainer p::before {
            content: "";
            position: absolute;
            left: 4px;
            top: 50%;
            transform: translateY(-50%);
            width: 14px;
            height: 14px;
            border: 1px solid #3a4252;
            border-radius: 2px;
            background: #0a0f20;
            transition: all 0.2s ease;
        }

        /* Checkbox Checked State */
        .dark-pivot-theme .pvtCheckContainer p.selected::before {
            background: #2563eb !important;
            border-color: #2563eb !important;
        }

        /* Checkmark icon using pseudo-element */
        .dark-pivot-theme .pvtCheckContainer p.selected::after {
            content: "✓";
            position: absolute;
            left: 6px; /* Adjusted to follow checkbox position */
            top: 50%;
            transform: translateY(-50%);
            color: white;
            font-size: 9px;
            font-weight: bold;
        }

        /* Checkbox Partial State (Mixed) */
        .dark-pivot-theme .pvtCheckContainer p.partial-selected::before {
            background: #1e3a8a !important; /* Dimmed blue */
            border-color: #3b82f6 !important;
        }

        .dark-pivot-theme .pvtCheckContainer p.partial-selected::after {
            content: "−"; /* Em dash for indeterminate state */
            position: absolute;
            left: 6px;
            top: 50%;
            transform: translateY(-50%);
            color: #93c5fd;
            font-size: 10px;
            font-weight: bold;
        }

        .dark-pivot-theme .pvtCheckContainer p.selected {
            background: #1e293b !important;
            color: #ffffff !important;
        }

        .dark-pivot-theme .pvtCheckContainer p:hover {
            background: #283046 !important;
        }

        /* Chart container bg and full height */
        .dark-pivot-theme .js-plotly-plot {
            width: 100% !important;
            height: 100% !important;
            min-height: 100% !important;
        }
        .dark-pivot-theme .js-plotly-plot .plotly {
            width: 100% !important;
            height: 100% !important;
        }
        .dark-pivot-theme .js-plotly-plot .plotly .gtitle,
        .dark-pivot-theme .js-plotly-plot .plotly .xtitle,
        .dark-pivot-theme .js-plotly-plot .plotly .ytitle,
        .dark-pivot-theme .js-plotly-plot .plotly .xtick text,
        .dark-pivot-theme .js-plotly-plot .plotly .ytick text,
        .dark-pivot-theme .js-plotly-plot .plotly .legendtext {
          fill: #e2e8f0 !important;
        }

        /* 1. Target the Trace Name Box (Grey Part) and turn it DARK */
        /* Use broad selector for anything that looks like a secondary grey label box */
        .js-plotly-plot .hovertext path.name,
        .js-plotly-plot .hovertext path[fill*="rgb(234"],
        .js-plotly-plot .hovertext path[fill*="rgb(240"],
        .js-plotly-plot .hovertext path[fill*="#ea"],
        .js-plotly-plot .hovertext path[style*="fill: rgb(234"],
        .js-plotly-plot .hovertext path[style*="fill: #ea"] {
            fill: #0a0f20 !important;
            fill-opacity: 1 !important;
            stroke: #475569 !important;
        }

        /* 2. Enforce LIGHT text on ALL parts of the tooltip for readability */
        .js-plotly-plot .hovertext text {
          fill: #ffffff !important;
          font-weight: 500 !important;
        }

        /* 3. Do NOT target generic 'path' inside hovertext to allow 
           Plotly's data bubble to inherit trace color via its own styles */
        
        .dark-pivot-theme .js-plotly-plot .plotly .gridlayer path {
          stroke: #334155 !important;
        }
        /* Absolute override to remove horizontal borders from data table */
        .dark-pivot-theme table.pvtTable tbody tr td,
        .dark-pivot-theme table.pvtTable tbody tr th,
        .dark-pivot-theme table.pvtTable .pvtRowLabel {
            border-top: none !important;
            border-bottom: none !important;
        }

      `}</style>
    </div>
  );
}

// Forced reload
